import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  type ReactNode,
} from "react";
import type { SurfaceToken } from "../../components/ui/box-classes";
import type { WidgetConfig } from "./types";

/**
 * The kit's per-slot instance context: which placed widget am I (`instanceId`
 * + persisted `config`)? Provided automatically by `WidgetGrid` around every
 * widget slot; hosts that mount a bare `<Widget>` for a real placed widget
 * provide it themselves.
 *
 * Domain semantics (what data situation the widget is in) are NOT here — a
 * consuming domain layers its own context alongside this one.
 */

export type WidgetInstanceContextValue = {
  /** The placed widget's persisted identity. */
  instanceId: string;
  /** The widget's persisted config blob (raw, pre-resolution). */
  config: WidgetConfig | null;
};

const WidgetInstanceContext = createContext<WidgetInstanceContextValue | null>(null);

/** Provide one placed widget's identity. `WidgetGrid` mounts this per slot. */
export function WidgetInstanceProvider({
  instanceId,
  config,
  children,
}: {
  instanceId: string;
  config: WidgetConfig | null;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ instanceId, config }), [instanceId, config]);
  return <WidgetInstanceContext.Provider value={value}>{children}</WidgetInstanceContext.Provider>;
}

/**
 * The wrapping placed widget's identity, or `null` outside any provider (a
 * widget body rendered with no placement — e.g. a settings preview). Never
 * throws.
 */
export function useWidgetInstance(): WidgetInstanceContextValue | null {
  return useContext(WidgetInstanceContext);
}

/**
 * The surface the wrapping `WidgetCard` painted — what a body's own opaque
 * parts (a table's sticky header, pinned cells) must paint to blend with the
 * card instead of cutting a differently-filled hole. `WidgetCard` provides
 * its ONE resolved value (the surface it paints, or the board's surface for a
 * canvas card); outside any card the body sits on the page.
 */
const WidgetSurfaceContext = createContext<SurfaceToken>("background");

export function WidgetSurfaceProvider({
  value,
  children,
}: {
  value: SurfaceToken;
  children: ReactNode;
}) {
  return <WidgetSurfaceContext.Provider value={value}>{children}</WidgetSurfaceContext.Provider>;
}

/** The surface the wrapping widget card painted (`background` outside a card). */
export function useWidgetSurface(): SurfaceToken {
  return useContext(WidgetSurfaceContext);
}

/**
 * Load-state feed from a widget body to its measuring card. `WidgetSkeleton`
 * registers itself here while mounted, so an autoHeight card knows the
 * heights it reports are placeholder heights: those commits — and the first
 * real measurement after the skeleton leaves — settle the grid without the
 * FLIP spring (content arriving is not a user action, so nothing should
 * appear to slide). `null` outside a measuring card (bare bodies, previews,
 * fixed-height cards): the skeleton no-ops.
 */
export type WidgetBodyLoadingRegistry = {
  enter: () => void;
  exit: () => void;
};

const WidgetBodyLoadingContext = createContext<WidgetBodyLoadingRegistry | null>(null);

/** Provided by a measuring `WidgetCard` around its body. */
export function WidgetBodyLoadingProvider({
  value,
  children,
}: {
  value: WidgetBodyLoadingRegistry | null;
  children: ReactNode;
}) {
  return (
    <WidgetBodyLoadingContext.Provider value={value}>
      {children}
    </WidgetBodyLoadingContext.Provider>
  );
}

/** The wrapping measuring card's load-state registry, or `null` outside one. */
export function useWidgetBodyLoading(): WidgetBodyLoadingRegistry | null {
  return useContext(WidgetBodyLoadingContext);
}

/**
 * Report the calling body as loading for as long as `loading` holds. Every
 * placeholder a body can render — `WidgetSkeleton`, a `StatBand` awaiting its
 * figures, a `WidgetDisclosureRowSkeleton` — registers through this one hook,
 * so the measuring card learns about all of them the same way. Layout effect:
 * the exit must be recorded before the ResizeObserver measures the content
 * that replaces the placeholder. A no-op outside a measuring card.
 */
export function useWidgetBodyLoadingWhile(loading: boolean): void {
  const registry = useWidgetBodyLoading();
  useLayoutEffect(() => {
    if (!loading || !registry) return undefined;
    registry.enter();
    return registry.exit;
  }, [loading, registry]);
}
